import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Layers3 } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import CustomSelect from "../../components/form/CustomSelect";
import { formatDate } from "./shared";

/**
 * Import a whole location at once: every georeferenced PDF sheet under it is
 * read and built as a draft, with one set of settings — optionally copied
 * from an import already checked by hand (the template). Nothing is
 * published until the batch has been reviewed (GisImport/BatchDetail.js).
 */

const BATCH_STATUS = {
  running: { bg: "info", label: "Running" },
  paused: { bg: "warning", label: "Paused" },
  done: { bg: "primary", label: "Ready to review" },
  published: { bg: "success", label: "Published" },
};

export default function BulkImportCard({ mapTypeOptions, defaultMapTypeId }) {
  const navigate = useNavigate();
  const toast = useToast();

  const [mapTypeId, setMapTypeId] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  const [location, setLocation] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [authority, setAuthority] = useState("");
  const [planName, setPlanName] = useState("");
  const [templates, setTemplates] = useState([]);
  // "own": every sheet uses what is detected on it (plus the authority's
  // saved zone codes). "copy": settings of a checked import of the same authority.
  const [settingsMode, setSettingsMode] = useState("own");
  const [templateId, setTemplateId] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [starting, setStarting] = useState(false);
  const [batches, setBatches] = useState([]);
  const [showSkipped, setShowSkipped] = useState(false);

  useEffect(() => {
    if (defaultMapTypeId) setMapTypeId((current) => current || defaultMapTypeId);
  }, [defaultMapTypeId]);

  useEffect(() => {
    Axios.get("/maps/gis-imports/batches", authHeaders())
      .then((res) => setBatches(res.data?.data || []))
      .catch((err) => console.error("Failed to fetch bulk imports", err));
    Axios.get("/maps/gis-imports/profiles", authHeaders())
      .then((res) => setProfiles(res.data?.data || []))
      .catch(() => {});
    // Templates: single-sheet imports already built or published.
    Axios.get("/maps/gis-imports", { params: { limit: 100 }, ...authHeaders() })
      .then((res) =>
        setTemplates((res.data?.data?.data || []).filter((r) => ["built", "published"].includes(r.status)))
      )
      .catch((err) => console.error("Failed to fetch imports", err));
  }, []);

  useEffect(() => {
    if (!mapTypeId) {
      setLocationOptions([]);
      return undefined;
    }
    const handler = setTimeout(async () => {
      try {
        const res = await Axios.get("/maps/map-locations/all", {
          params: { map_type_id: mapTypeId, search: locationSearch, last_level_only: true, page: 1, limit: 50 },
          ...authHeaders(),
        });
        setLocationOptions(
          (res.data?.data || []).map((l) => ({ value: l.id, label: l.location_path || l.name, name: l.name }))
        );
      } catch (err) {
        console.error("Failed to load locations", err);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [mapTypeId, locationSearch]);

  useEffect(() => {
    setPlan(null);
    setShowSkipped(false);
    if (!location) return;
    setPlanning(true);
    Axios.get("/maps/gis-imports/batches/plan", { params: { location_id: location.value }, ...authHeaders() })
      .then((res) => setPlan(res.data?.data || null))
      .catch((err) => toast.error(err.response?.data?.message || "Could not check the location"))
      .finally(() => setPlanning(false));
  }, [location, toast]);

  // Only imports of the authority typed: another authority's template would
  // label these sheets' colours with its own zone codes.
  const templateOptions = useMemo(
    () =>
      templates
        .filter((t) => authority && t.authority === authority)
        .map((t) => ({
          value: t.id,
          label: `${t.map?.title} · ${t.plan_name || ""} (${t.authority || "-"}) · ${t.status}`,
          authority: t.authority,
          planName: t.plan_name,
        })),
    [templates, authority]
  );

  const start = async (e) => {
    e.preventDefault();
    setStarting(true);
    try {
      const res = await Axios.post(
        "/maps/gis-imports/batches",
        {
          location_id: location.value,
          authority: authority.trim(),
          plan_name: planName.trim(),
          template_import_id: settingsMode === "copy" ? templateId || undefined : undefined,
          profile_id: settingsMode === "profile" ? profileId || undefined : undefined,
        },
        authHeaders()
      );
      navigate(`/gis-imports/batches/${res.data?.data?.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not start the bulk import");
    } finally {
      setStarting(false);
    }
  };

  const canStart =
    location &&
    plan?.take?.length > 0 &&
    authority.trim() &&
    planName.trim() &&
    (settingsMode === "own" || (settingsMode === "copy" && templateId) || (settingsMode === "profile" && profileId)) &&
    !starting;

  return (
    <div className="bg-white rounded-2 common-shadow mt-4 p-4">
      <h6 className="fw-semibold mb-1 d-flex align-items-center gap-2">
        <Layers3 size={17} /> Import a whole location
      </h6>
      <p className="text-muted small mb-3" style={{ maxWidth: 760 }}>
        Every georeferenced PDF sheet under the location is read and built as a draft, two at a time. Each sheet uses
        its own detected settings, or — once you have checked one sheet of this authority by hand — that sheet's
        settings. You review the results before anything is published.
      </p>

      <Form onSubmit={start} style={{ maxWidth: 760 }}>
        <div className="row g-3">
          <div className="col-md-6">
            <CustomSelect
              label={{ name: "Map type" }}
              options={mapTypeOptions}
              value={mapTypeOptions.find((o) => o.value === mapTypeId) || null}
              onChange={(o) => {
                setMapTypeId(o?.value || "");
                setLocation(null);
              }}
              placeholder="Choose a map type"
            />
          </div>
          <div className="col-md-6">
            <CustomSelect
              label={{ name: "Location" }}
              required
              isDisabled={!mapTypeId}
              options={locationOptions}
              value={location}
              onChange={(o) => setLocation(o || null)}
              onInputChange={(v, meta) => {
                if (meta.action === "input-change") setLocationSearch(v);
              }}
              placeholder="Search a location"
              isClearable
            />
          </div>

          {planning && (
            <div className="col-12 small text-muted d-flex align-items-center gap-2">
              <Spinner size="sm" /> Checking the sheets...
            </div>
          )}
          {plan && (
            <div className="col-12">
              <Alert variant={plan.take.length ? "light" : "warning"} className="small py-2 mb-0">
                <span className="fw-semibold">{plan.take.length}</span> sheet{plan.take.length === 1 ? "" : "s"} will
                be imported.
                {plan.skipped.length > 0 && (
                  <>
                    {" "}
                    {plan.skipped.length} skipped{" "}
                    <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => setShowSkipped((v) => !v)}>
                      ({showSkipped ? "hide" : "show why"})
                    </Button>
                  </>
                )}
                {showSkipped && (
                  <ul className="mb-0 mt-2">
                    {plan.skipped.map((s) => (
                      <li key={s.map_id}>
                        {s.title}: {s.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </Alert>
            </div>
          )}

          <div className="col-md-4">
            <Form.Label className="small fw-medium">Authority</Form.Label>
            <Form.Control
              value={authority}
              maxLength={30}
              onChange={(e) => {
                setAuthority(e.target.value.toUpperCase());
                setTemplateId("");
                setProfileId("");
              }}
              placeholder="e.g. AUDA"
              required
            />
          </div>
          <div className="col-md-8">
            <Form.Label className="small fw-medium">Plan name</Form.Label>
            <Form.Control
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g. Ahmedabad DP 2021"
              required
            />
          </div>
          <div className="col-12">
            <Form.Label className="small fw-medium d-block">Settings</Form.Label>
            <Form.Check
              type="radio"
              id="bulk-settings-own"
              name="bulk-settings"
              checked={settingsMode === "own"}
              onChange={() => setSettingsMode("own")}
              label={
                <span>
                  Each sheet's own settings{" "}
                  <span className="text-muted small">
                    — layers, zone colours and labels detected on every sheet, plus {authority || "the authority"}'s saved
                    zone codes. Use this for a new authority.
                  </span>
                </span>
              }
            />
            <Form.Check
              type="radio"
              id="bulk-settings-profile"
              name="bulk-settings"
              className="mt-1"
              checked={settingsMode === "profile"}
              onChange={() => setSettingsMode("profile")}
              label={
                <span>
                  Use a profile{" "}
                  <span className="text-muted small">— your own saved settings for this kind of sheet</span>
                </span>
              }
            />
            {settingsMode === "profile" && (
              <div className="mt-2 ms-4">
                <CustomSelect
                  options={profiles
                    .filter((p) => authority && p.authority === authority)
                    .map((p) => ({ value: p.id, label: `${p.name} · v${p.version}` }))}
                  value={(() => {
                    const p = profiles.find((x) => x.id === profileId);
                    return p ? { value: p.id, label: `${p.name} · v${p.version}` } : null;
                  })()}
                  onChange={(o) => setProfileId(o?.value || "")}
                  isDisabled={!authority}
                  placeholder={authority ? `A ${authority} profile` : "Type the authority first"}
                  noOptionsMessage={() => `No ${authority} profile yet — create one under Import profiles`}
                  isClearable
                />
              </div>
            )}
            <Form.Check
              type="radio"
              id="bulk-settings-copy"
              name="bulk-settings"
              className="mt-1"
              checked={settingsMode === "copy"}
              onChange={() => setSettingsMode("copy")}
              label={
                <span>
                  Copy from a checked import{" "}
                  <span className="text-muted small">— layer choices, label sizes and zone codes of one sheet you have already checked</span>
                </span>
              }
            />
            {settingsMode === "copy" && (
              <div className="mt-2 ms-4">
                <CustomSelect
                  options={templateOptions}
                  value={templateOptions.find((o) => o.value === templateId) || null}
                  onChange={(o) => setTemplateId(o?.value || "")}
                  isDisabled={!authority}
                  placeholder={authority ? `A checked ${authority} import` : "Type the authority first"}
                  noOptionsMessage={() => `No built or published ${authority} import yet — use each sheet's own settings`}
                  isClearable
                />
                <Form.Text muted>Only imports of the same authority are offered.</Form.Text>
              </div>
            )}
          </div>
        </div>

        <Button type="submit" className="mt-4 d-flex align-items-center gap-2" disabled={!canStart}>
          {starting ? <Spinner size="sm" /> : <Layers3 size={16} />}
          Import {plan?.take?.length || ""} sheets as drafts
        </Button>
      </Form>

      {batches.length > 0 && (
        <>
          <h6 className="fw-semibold mt-4 mb-2">Bulk imports</h6>
          <Table hover responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Location</th>
                <th>Plan</th>
                <th>Started</th>
                <th>Status</th>
                <th>Sheets</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const st = BATCH_STATUS[b.status] || { bg: "secondary", label: b.status };
                return (
                  <tr key={b.id} role="button" style={{ cursor: "pointer" }} onClick={() => navigate(`/gis-imports/batches/${b.id}`)}>
                    <td className="fw-medium">{b.location_name || "-"}</td>
                    <td>
                      {b.plan_name} <span className="text-muted small">· {b.authority}</span>
                    </td>
                    <td>{formatDate(b.created_at)}</td>
                    <td>
                      <span className={`badge bg-${st.bg} fw-medium rounded-1`}>{st.label}</span>
                    </td>
                    <td className="small text-muted">
                      {b.sheets} · {b.built} built · {b.published} published{b.failed ? ` · ${b.failed} failed` : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
}
